import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { connect } from 'redux-bundler-react';
import { debounce } from '../tableCellHelper';
import { decimalNumberRegex } from '@src/utils/regex';

const GeneticVialNumTableCell = connect('selectBaseData', ({ baseData, getValue, row, column, table, cell }) => {
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue ?? '');

  const rowSpecies = useMemo(() => row.getValue('species'), [row]);
  const previousSpecies = useRef(rowSpecies);

  const { projectId, season } = baseData;
  const project = Number(projectId);

  const debouncedUpdateRef = useRef();

  const prefix = value?.split('-')?.[0] ?? '';
  const number = value?.split('-')?.[1] ?? '';
  const isRequired = rowSpecies === 'USG';

  const updateValue = useCallback((newValue) => {
    debouncedUpdateRef.current(newValue);
  }, []);

  const handleBlur = (e) => {
    // Clear field if value is 0 or is a negative number
    if (String(e?.target?.value) === '0' || String(e?.target?.value)[0] === '-') {
      setValue(`${prefix}-`);
      updateValue(`${prefix}-`);
    }
  };

  const handlePrefixChange = (e) => {
    // Convert the input value to uppercase and update the state
    const val = e?.target?.value;
    const uppercaseValue = val?.toUpperCase();
    setValue(`${uppercaseValue}-${number}`);
    updateValue(`${uppercaseValue}-${number}`);
  };

  const handleNumberChange = (e) => {
    const val = e?.target?.value ?? '';
    if (decimalNumberRegex.test(val) || val === '') {
      setValue(val === '' ? `${prefix}-` : `${prefix}-${val}`);
      updateValue(val === '' ? `${prefix}-` : `${prefix}-${val}`);
    }
  };

  useEffect(() => {
    debouncedUpdateRef.current = debounce((newValue) => {
      if (tableMeta?.updateData) {
        tableMeta?.updateData(row.index, column.id, newValue ?? newValue);
      }
    }, 500);
  }, [row.index, column.id, tableMeta?.updateData, columnMeta?.type, tableMeta]);

  useEffect(() => {
    const previousRowSpecies = previousSpecies.current;
    const speciesChangedToUsg = previousRowSpecies !== 'USG' && rowSpecies === 'USG';
    const speciesChangedAwayFromUsg = previousRowSpecies === 'USG' && rowSpecies !== 'USG';
    const currentValue = value === '' ? initialValue ?? '' : value;
    const [currentPrefix = '', currentNumber = ''] = currentValue.split('-');

    previousSpecies.current = rowSpecies;

    if (speciesChangedToUsg && !currentPrefix && !currentNumber && project === 1 && season === 'MR') {
      const nextValue = 'STURG-';
      setValue(nextValue);
      updateValue(nextValue);
    } else if (speciesChangedAwayFromUsg && currentPrefix === 'STURG' && !currentNumber) {
      const nextValue = '';
      setValue(nextValue);
      updateValue(nextValue);
    }
  }, [rowSpecies, project, season, updateValue, value, initialValue]);

  // Sync value state when initialValue changes (for loading new records)
  useEffect(() => {
    setValue(initialValue ?? '');
  }, [initialValue]);

  return (
    <>
      <input
        aria-label='Genetic Vial Number Prefix'
        disabled={columnMeta?.readOnly || !isRequired}
        id={cell.id}
        maxLength={10}
        onChange={handlePrefixChange}
        required={isRequired}
        style={{
          width: '100%',
          borderColor: 'hsl(0, 0%, 80%)',
          cursor: columnMeta?.readOnly ? 'not-allowed' : 'auto',
        }}
        type='text'
        value={prefix}
      />
      <div className='text-bold' style={{ fontSize: '20px' }}>
        -
      </div>
      <input
        aria-label='Genetic Vial Number'
        disabled={!isRequired}
        id={cell.id}
        maxLength={5}
        onBlur={handleBlur}
        onChange={handleNumberChange}
        required={isRequired}
        style={{
          width: '100%',
          borderColor: 'hsl(0, 0%, 80%)',
          cursor: columnMeta?.readOnly ? 'not-allowed' : 'auto',
        }}
        type='text'
        value={number ?? ''}
      />
    </>
  );
});

export default GeneticVialNumTableCell;
